import { CurrentEntryPoint as PayEntryPoint } from '@scorewarrior/rubens_web_lib/lib/accounts/current-entry-point';
import { FbPaymentBuyStatus, PayServiceNetworkError } from '@scorewarrior/rubens_web_lib/lib/payservice';
import {
    FacebookLogInResponse,
    FacebookLoginStatus, FbAuthResponse,
    FbPayUiResult,
    FbPermissionEntry,
    FbPermissionsResponse, FbSyncBuyResponse,
    FbUserFieldsResponse
} from './sdk-types';
import { UserData } from '../../general/BaseUserModel';
import { Gender } from '../../constants/Gender';
import { PopupIds } from '../../constants/PopupIds';
import { CurrentEntryPoint } from '../../constants/CurrentEntryPoint';
import { Header } from '../../layout/header';
import { SocialAdapterInterface } from '../social-adapter-interface';
import * as $script from '../../vendor/script.min';
import { WebPreloader } from '../../popups/webPreloader';
import { getUserSuid } from '../../general/modules/getUserSuid';
import { onPayFinish } from '../../general/game_general';
import { PaymentStatus } from '../../constants/paymentStatus';
import { PaymentObject } from '../../types/payments';
import { PaymentServices } from '../../payments/paymentServices';
import { getInitiatedTransactionId } from '../../general/orderbox/paymentsHelper';
import { getPayServiceClient } from '../../payments/payServiceClient';
import { container } from '../../utils/container/container';
import { ServiceIds } from '../../utils/container/service-ids';
import { FeatureFlagsLoader } from '../../utils/feature-flags/feature-flags-loader';
import { FlagNames } from '../../utils/feature-flags/flag-names';

const MAX_NATIVE_FB_TRIES = 5;
let triedLoadingHostedSdk = false;
let currentTry = 0;

const nativeSdkUrl = `//connect.facebook.net/${window.app.config.locale}/sdk.js`;
const version = window.app.config?.fb?.fbsdkversion ?? '';
const backupSdkLink = `${window.app.config.static_path + window.app.config.fb.static_part_path + version}.js`;

export class FbAdapter implements SocialAdapterInterface {
    private appId: number;

    public constructor(appId: number) {
        this.appId = appId;
    }

    public initWidgets() {
        console.log('init widgets stub');
    }

    public async loadSdk(scriptUrl: string = nativeSdkUrl): Promise<void> {
        try {
            await new Promise((resolve: Function, reject: Function) => {
                $script([scriptUrl], () => {
                    const fbApiAvailable = typeof window.FB === 'undefined';

                    if (fbApiAvailable) {
                        reject(new Error('Failed to load fb sdk'));
                        return;
                    }

                    resolve();
                });
            });
        } catch (error) {
            currentTry += 1;

            if (currentTry < MAX_NATIVE_FB_TRIES) {
                return this.loadSdk();
            }

            if (!triedLoadingHostedSdk) {
                triedLoadingHostedSdk = true;
                return this.loadSdk(backupSdkLink);
            }

            return Promise.reject(error);
        }
    }

    public initSdk() {
        window.FB.init({
            appId: this.appId,
            xfbml: true,
            version: window.app.config.fb.gapi_version,
            frictionlessRequests: true,
            status: true
        });
        window.FB.Canvas.Prefetcher.setCollectionMode(window.FB.Canvas.Prefetcher.COLLECT_MANUAL);
    }

    public logIn(): Promise<boolean> {
        return new Promise((resolve: Function, reject: Function) => {
            window.FB.login(function handleLoginResponse(response: FacebookLogInResponse) {
                try {
                    console.log(response);
                    if (response.status === 'connected') {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                } catch (error) {
                    reject(error);
                }
            }, {
                scope: window.app.config.fb.scope
            });
        });
    }

    public async getAuthData(): Promise<FbAuthResponse> {
        const loginStatus: FbAuthResponse = await window.FB.getAuthResponse();
        return loginStatus;
    }

    public async getIsLoggedIn(): Promise<boolean> {
        const status: FacebookLoginStatus = await this.getLoginStatus();
        console.log('Facebook login status', status);
        return status === 'connected';
    }

    public async getLoginStatus(): Promise<FacebookLoginStatus> {
        return new Promise((resolve: Function, reject: Function) => {
            window.FB.getLoginStatus(
                function setLoginStatus(response: FacebookLogInResponse) {
                    try {
                        resolve(response.status);
                    } catch (error) {
                        reject(error);
                    }
                },
                true
            );
        });
    }

    public getPermissions(): Promise<FbPermissionEntry[]> {
        return new Promise((resolve: Function, reject: Function) => {
            window.FB.api('/me/permissions', (response: FbPermissionsResponse): boolean | void => {
                try {
                    resolve(response.data);
                } catch (error) {
                    reject(error);
                }
            });
        });
    }

    public async getPermission(permissionName: string): Promise<boolean|null> {
        const permissions = await this.getPermissions();
        const permissionEntry = permissions.find((entry: FbPermissionEntry) => {
            return entry.permission === permissionName;
        });

        return permissionEntry && permissionEntry.status === 'granted';
    }

    public getUserData(): Promise<UserData> {
        const fields = window.app.config.fb.fields;
        return new Promise((resolve: Function, reject: Function) => {
            window.FB.api(
                `/me?fields=${fields}`,
                (result: FbUserFieldsResponse) => {
                    try {
                        if (result?.error) {
                            throw new Error(`Failed to get user fields: ${result.error.message}`);
                        }

                        let gender = 0;
                        if (result?.gender) {
                            gender = (result.gender === 'male') ? Gender.MALE : Gender.FEMALE;
                        }

                        const userData: UserData = {
                            age: result.birthday
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                ? parseInt((new Date().getFullYear()) as any) - parseInt(result.birthday.split('/')[2])
                                : 0,
                            city: '',
                            email: result?.email ?? null,
                            country: window.app.config.fb.fb_country,
                            first_name: result.first_name,
                            full_name: `${result.first_name} ${result.last_name}`,
                            gender,
                            last_name: result.last_name,
                            photo: result?.picture.data.url ?? window.app.config.default_picture
                        };

                        resolve(userData);
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    }

    public askPermission(permissionName: string): Promise<boolean> {
        return new Promise((resolve: Function, reject: Function) => {
            window.FB.login(function handleLoginResponse(response: FacebookLogInResponse) {
                try {
                    resolve(response.authResponse.grantedScopes.includes(permissionName));
                } catch (error) {
                    reject(error);
                }
            }, {
                auth_type: 'rerequest',
                return_scopes: true,
                scope: permissionName
            });
        });
    }

    public registerEventHandlers(): void {
        try {
            window.FB.Event.subscribe('edge.create', function handlePageLike() {
                console.log('page_liked');
                window.app.user.isMember = true;
                window.app.game_popup_close(PopupIds.FB_LIKE_PAGE);
            });
            window.FB.Event.subscribe('edge.remove', function handlePageUnlike() {
                window.app.user.isMember = false;
            });
        } catch (error) {
            window.Sentry.captureException(error);
            console.error(error);
        }

        window.app.fb_purchase_info = function fbPurchaseInfo(name: string, number: number): boolean | void {
            try {
                window.FB.AppEvents.logEvent(
                    `${window.app.config.current_entry_point}_purchase_info`,
                    null,
                    {
                        NAME: name,
                        PCOUNT: number
                    }
                );
                console.log('window.app.fb_purchase_info');
                return true;
            } catch (error) {
                console.warn(`window.app.fb_purchase_info${(<Error>error).message}`);
            }
        };

        window.app.fb_tutorial_complete = function fbTutorialComplete(): boolean | void {
            try {
                window.FB.AppEvents.logEvent(
                    `${window.app.config.current_entry_point}_tutorial_complete`,
                    null,
                    {
                        SUCCESS: 1
                    }
                );
                console.log('window.app.fb_tutorial_complete');
                return true;
            } catch (error) {
                console.warn(`window.app.fb_tutorial_complete${(<Error>error).message}`);
            }
        };

        window.app.fb_capitol_upgraded = function fbCapitolUpgraded(level: number): boolean | void {
            try {
                window.FB.AppEvents.logEvent(
                    `${window.app.config.current_entry_point}_capitol_upgraded`,
                    null,
                    {
                        LEVEL: level
                    }
                );
                console.log(`window.app.fb_capitol_upgraded level:${level}`);
                return;
            } catch (error) {
                console.warn(`window.app.fb_capitol_upgraded${(<Error>error).message}`);
            }
        };

        window.app.fb_remarketing_offer_taken = function fbRemarketingOfferTaken(
            count: number,
            ref: string
        ): boolean | void {
            try {
                window.FB.AppEvents.logEvent(
                    `${window.app.config.current_entry_point}_remarketing_offer_taken`,
                    null,
                    {
                        COUNT: count,
                        REF: ref
                    }
                );
                console.log('window.app.fb_remarketing_offer_taken', count);
                return true;
            } catch (error) {
                console.warn(`window.app.fb_remarketing_offer_taken${(<Error>error).message}`);
            }
        };

        let count: number;

        try {
            const isRemarketingVisit = window.app.config.ref.search(/_RM_/i) !== -1;
            if (isRemarketingVisit) {
                count = 1;

                if ($.cookie('fb_rot')) {
                    count = $.cookie('fb_rot', Number) as number;
                    count += 1;
                }

                $.cookie('fb_rot', count, {
                    secure: true
                });

                window.app.fb_remarketing_offer_taken(count, window.app.config.ref);
            }
        } catch (error) {
            console.warn(error);
            window.Sentry.captureException(error);
        }

        $('.js-header-invite-friend').on('click', async() => {
            if (await this.getIsLoggedIn()) {
                FbAdapter.showInviteFriendDialog();
            }
        });

        $('#fb_like_page').find('.js-close-button')
            .on('click', function closeLikeWidget() {
                window.app.game_popup_close(PopupIds.FB_LIKE_PAGE);
            });

        window.FB.Event.subscribe('xfbml.render', function recalcHeaderSlot() {
            Header.calculateResponsiveCenterSlot();
        });
    }

    public startPayment = async(pmObject: PaymentObject) => {
        console.log(['payobj'], [pmObject]);
        WebPreloader.enable();
        window.app.last_payment = pmObject.id;
        if (!pmObject.guid_receiver) {
            pmObject.guid_receiver = window.app.getGameStartDataResponse.guid;
        }
        /*
         * cтавим дефолтные значения еcли они потеряны
         * */
        if (!pmObject.name) {
            pmObject.name = 'Sale 80%';
        }

        if (!$.trim(pmObject.description)) {
            pmObject.description = 'Take the power and crush your enemies!';
        }

        if (!pmObject.pic || pmObject.pic.length < 4) {
            pmObject.pic = 'default.png';
        }
        /**/

        const transactionId = await getInitiatedTransactionId(
            pmObject,
            PaymentServices.FB_PAY
        );

        if (!transactionId) {
            return;
        }
        const paymentInfo = [
            transactionId
        ];

        const ffLoader = container.get<FeatureFlagsLoader>(ServiceIds.FEATURE_FLAG_LOADER);
        const product = ffLoader.get(FlagNames.FB_PAYMENT_BUY_V2).isOn()
            ? window.app.config.fb.dynamic_price_url_v2 + paymentInfo.join('/')
            : window.app.config.fb.dynamic_price_url + paymentInfo.join('/');
        window.app.fireInitiateCheckout(pmObject);
        window.FB.ui(
            {
                action: 'purchaseitem',
                method: 'pay',
                product: product,
                quantity: 1,
                request_id: transactionId
            },
            this.getFbPayUiCallback(pmObject)
        );
    };

    private static async fbPaymentBuyV2(pmObject: PaymentObject, result: FbPayUiResult): Promise<void> {
        try {
            const buyResult = await getPayServiceClient().fbPaymentBuy({
                payEntryPoint: FbAdapter.resolvePayEntryPoint(),
                signed_request: result.signed_request,
                suid: getUserSuid(true),
                transaction_id: result.payment_id
            });

            if (buyResult.status === FbPaymentBuyStatus.COMPLETED) {
                onPayFinish(PaymentStatus.SUCCESS, pmObject.id);
            } else if (buyResult.status === FbPaymentBuyStatus.INITIATED) {
                onPayFinish(PaymentStatus.INITIATED, pmObject.id);
            } else {
                onPayFinish(PaymentStatus.FAIL, pmObject.id, 666);
            }
        } catch (error) {
            console.error(error);

            if (error instanceof PayServiceNetworkError) {
                onPayFinish(PaymentStatus.INITIATED, pmObject.id, '');
            } else {
                onPayFinish(PaymentStatus.FAIL, pmObject.id, 666);
            }
        } finally {
            WebPreloader.disable();
        }
    }

    private static resolvePayEntryPoint(): PayEntryPoint.FB | PayEntryPoint.FB2 {
        return window.app.config.current_entry_point === CurrentEntryPoint.FB2 ? PayEntryPoint.FB2 : PayEntryPoint.FB;
    }

    private static showInviteFriendDialog() {
        window.FB.ui(
            {
                method: 'apprequests',
                message: 'Come play!'
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            function handleFbAppRequestResponse(response: any) {
                console.log('apprequests', response);
            }
        );
    }

    private getFbPayUiCallback: (pmObject: PaymentObject) => (result: FbPayUiResult) => void
        = (pmObject: PaymentObject) => {
            return function fbPayUiCallback(result: FbPayUiResult) {
                const FB_PAYMENT_CANCELLED_CODE = 1383010;
                let params;

                if (!result) {
                    throw new Error('Unexpected fb pay ui callback call, no argument');
                }

                if (result?.error_code) {
                    WebPreloader.disable();
                // https://developers.facebook.com/docs/games_payments/fulfillment/errorcodes
                    if (result.error_code === FB_PAYMENT_CANCELLED_CODE) {
                        onPayFinish(PaymentStatus.CANCEL);
                    } else {
                        if (result.error_message && result.error_message.length >= 1) {
                            onPayFinish(PaymentStatus.FAIL, pmObject.id, result.error_message);
                        } else {
                            onPayFinish(PaymentStatus.FAIL, pmObject.id, result.error_code);
                        }
                    }

                    return;
                }

                if (!result.error_code) {
                    console.log('ui pay check');

                    const ffLoader = container.get<FeatureFlagsLoader>(ServiceIds.FEATURE_FLAG_LOADER);
                    if (ffLoader.get(FlagNames.FB_PAYMENT_BUY_V2).isOn()) {
                        FbAdapter.fbPaymentBuyV2(pmObject, result);
                        return;
                    }

                    params = {
                        suid: getUserSuid(true),
                        transaction_id: result.payment_id,
                        pm_id: pmObject.id,
                        signed_request: result.signed_request,
                        guid_receiver: pmObject.guid_receiver
                    };

                    $.post(
                        window.app.config.fb.sync_buy_url,
                        params,
                        function handleSyncBuyResult(response: FbSyncBuyResponse) {
                            if (response && !response.error) {
                                if (response.response.status === 'completed') {
                                    onPayFinish(PaymentStatus.SUCCESS, pmObject.id);
                                }
                                if (response.response.status === 'initiated') {
                                    onPayFinish(PaymentStatus.INITIATED, pmObject.id);
                                }
                            } else {
                                onPayFinish(PaymentStatus.FAIL, pmObject.id, 666);
                            }
                        }
                    )
                    // eslint-disable-next-line @typescript-eslint/typedef
                        .fail(function handleSyncBuyFail(xhr, textStatus, errorMessage) {
                            console.error(xhr, textStatus);
                            console.error(errorMessage);
                            onPayFinish(PaymentStatus.INITIATED, pmObject.id, '');
                        })
                        .always(function disableWebPreloader() {
                            WebPreloader.disable();
                        });
                }
            };
        };
}
